import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const STEPS = [
  { title: 'This is your dashboard', body: 'New leads from your website form and chatbot appear here automatically.' },
  { title: 'Open a lead to see details', body: 'Tap any lead to view their info — then call or email them in one tap.' },
  { title: 'Move leads forward', body: 'These buttons move a lead to prospect, then to customer.' },
  { title: 'Stay on top of follow-ups', body: 'Follow-ups you schedule show up right here on your dashboard.' },
];

const storageKey = (userId: string) => `essentials_tour_dismissed_${userId}`;

/** Dismissible 4-step overlay shown on first login only, persisted per user. */
const FirstRunTour: React.FC = () => {
  const { user } = useAuth();
  const userId = user?.id ?? '';
  const [step, setStep] = useState(0);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (!userId) return true;
    try {
      return localStorage.getItem(storageKey(userId)) === '1';
    } catch {
      return false;
    }
  });

  if (dismissed || !userId) return null;

  const finish = () => {
    try {
      localStorage.setItem(storageKey(userId), '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  const isLast = step === STEPS.length - 1;
  const current = STEPS[step];

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" role="dialog" aria-label="Getting started">
      <div className="absolute inset-0 bg-overlay/70" aria-hidden />
      <div className="relative w-full max-w-md rounded-2xl border border-border bg-surface p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-primary">
            Step {step + 1} of {STEPS.length}
          </span>
          <button type="button" onClick={finish} className="text-sm text-fg-muted hover:text-fg">
            Skip
          </button>
        </div>
        <h2 className="text-xl font-bold text-fg">{current.title}</h2>
        <p className="mt-2 text-fg-muted">{current.body}</p>

        <div className="mt-6 flex items-center justify-between">
          <div className="flex gap-1.5">
            {STEPS.map((_, i) => (
              <span key={i} className={`h-2 w-2 rounded-full ${i === step ? 'bg-primary' : 'bg-border'}`} />
            ))}
          </div>
          <button
            type="button"
            onClick={() => (isLast ? finish() : setStep((s) => s + 1))}
            className="btn-primary rounded-xl px-5 py-2 text-sm font-semibold"
          >
            {isLast ? 'Got it' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FirstRunTour;
